import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { resolveImage, formatINR } from "@/lib/api";
import { CheckCircle2, Package, Truck, Home, Loader2 } from "lucide-react";

function getPaymentLabel(order) {
  if (order.payment_method !== "Razorpay") return "Cash on Delivery";
  return order.payment_status === "paid" ? "Paid online (Razorpay)" : "Razorpay";
}

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="text-center py-32"><h1 className="font-serif text-3xl">Order not found</h1></div>;
  if (!order) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 lg:py-16" data-testid="order-confirmation-page">
      <div className="text-center mb-10">
        <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center bg-green-100 rounded-full">
          <CheckCircle2 className="w-9 h-9 text-green-600" />
        </div>
        <h1 className="font-serif text-4xl text-obsidian mb-2">Thank You!</h1>
        <p className="text-muted-foreground">Your order has been placed successfully.</p>
        <p className="mt-3 inline-block bg-obsidian text-white px-4 py-2 text-sm font-semibold tracking-wide rounded-sm" data-testid="order-number">
          Order #{order.order_number}
        </p>
      </div>

      {/* Steps */}
      <div className="flex items-center justify-between bg-white border border-black/10 p-5 mb-6 text-center">
        {[
          { icon: CheckCircle2, label: "Order Placed", active: true },
          { icon: Package, label: "Processing", active: false },
          { icon: Truck, label: "Shipped", active: false },
          { icon: Home, label: "Delivered", active: false },
        ].map((s) => (
          <div key={s.label} className="flex-1">
            <div className={`w-10 h-10 mx-auto flex items-center justify-center rounded-full ${s.active ? "bg-gold text-obsidian" : "bg-secondary text-muted-foreground"}`}>
              <s.icon className="w-5 h-5" />
            </div>
            <p className={`text-[11px] mt-2 ${s.active ? "text-obsidian font-semibold" : "text-muted-foreground"}`}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Items */}
      <div className="bg-white border border-black/10 p-6 mb-6">
        <h2 className="font-serif text-2xl mb-4">Order Summary</h2>
        <div className="space-y-4">
          {order.items.map((it) => (
            <div key={it.product_id} className="flex gap-3">
              <img src={resolveImage(it.image)} alt="" className="w-14 h-16 object-cover bg-secondary shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-obsidian">{it.name}</p>
                <p className="text-xs text-muted-foreground">{it.sku} · Qty {it.quantity}</p>
              </div>
              <span className="font-semibold">{formatINR(it.line_total)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-black/10 mt-5 pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Total MRP</span><span>{formatINR(order.subtotal_mrp)}</span></div>
          <div className="flex justify-between text-green-700"><span>You Saved</span><span>- {formatINR(order.total_savings)}</span></div>
          <div className="flex justify-between font-bold text-lg pt-2 border-t border-black/10"><span>Total</span><span>{formatINR(order.total)}</span></div>
          <div className="flex justify-between text-xs text-muted-foreground pt-1"><span>Payment</span><span>{getPaymentLabel(order)}</span></div>
        </div>
      </div>

      {/* Delivery */}
      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <InfoCard title="Delivery Address">
          <p className="text-sm text-obsidian font-medium">{order.customer.name}</p>
          {order.shipping_address ? (
            <>
              <p className="text-sm text-muted-foreground">{order.shipping_address.address}</p>
              <p className="text-sm text-muted-foreground">{order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.pincode}</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Our team will contact you to confirm your delivery address.</p>
          )}
          <p className="text-sm text-muted-foreground mt-1">{order.customer.mobile}</p>
        </InfoCard>
        <InfoCard title="What's Next">
          <p className="text-sm text-muted-foreground">We will confirm your order shortly and begin processing. You will receive updates as your order ships. Track it anytime under My Orders.</p>
        </InfoCard>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/account?tab=orders" className="inline-flex items-center justify-center gap-2 bg-obsidian text-white font-semibold px-7 py-3.5 hover:bg-gold hover:text-obsidian transition-colors rounded-sm" data-testid="view-orders-btn">View My Orders</Link>
        <Link to="/catalogue" className="inline-flex items-center justify-center gap-2 border border-obsidian text-obsidian font-semibold px-7 py-3.5 hover:bg-obsidian hover:text-white transition-colors rounded-sm">Continue Shopping</Link>
      </div>
    </div>
  );
}

const InfoCard = ({ title, children }) => (
  <div className="bg-white border border-black/10 p-6">
    <p className="overline text-gold-dark mb-3">{title}</p>
    {children}
  </div>
);
